import { useRouter } from "next/router";

import { timeOptions } from "@/views/job-add/components/JobDetailsForm";
import { CreateHiringManager } from "@/views/job-add/components/JobUsersForm";
import {
  RhDatePickerFormik,
  RhDivider,
  RhDrawer,
  RhFormGroup,
  RhFormGroupItem,
  RhIcon,
  RhInputFormik,
  RhLabel,
  RhScrollbar,
  RhSelectFormik,
  RhTabs,
  RhTabsItem,
  RhTimeline,
  RhTimelineItem,
  RhToast,
  RhTooltip,
} from "@rhythm-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Form, Formik } from "formik";
import React, { useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import * as Yup from "yup";

import { LocationPreferenceEnum } from "@/types/candidate";
import { JobFormType, JobStatusEnum, JobType } from "@/types/job";

import api from "@/services/api";
import {
  transformFormDataToJobData,
  transformJobDataToFormData,
} from "@/services/api/dataTransformers/jobModel";

import { HOST_URL, joiningAvailabilityOptions } from "@/constants";
import { locationPreferenceValues } from "@/constants/candidates";

import formatCurrencyRange from "@/helpers/formatCurrencyRange";
import formatExperienceRange from "@/helpers/formatExperienceRange";

import { propToFormatCityLabelWithRhSelect } from "@/components/component-helper/propToFormatCityLabelWithRhSelect";
import {
  citySearchApiCall,
  companyContactSearchApiCall,
  jobRoleSearchApiCall,
} from "@/components/dataInputApiCalls";
import CheckBoxInputFormik from "@/components/formikInput/CheckBoxInputFormik";
import CompanySelectFormik from "@/components/formikInput/CompanySelectFormik";
import DataSelectFormik from "@/components/formikInput/DataSelectFormik";
import RadioInputFormik from "@/components/formikInput/RadioInputFormik";
import RecruiterSelectFormik from "@/components/formikInput/RecruiterSelectFormik";
import JobSkillPrioritization from "@/components/JobSkillPrioritization";
import JobStatusProgress from "@/components/JobStatusProgress";
import { Button } from "@/components/ui/Button";
import RichTextEditor, { RichTextViewer } from "@/components/ui/RichTextEditor";
import { Chip } from "@/components/utils/Chip";

import AddNote from "./AddNote";
import ClientReportAnalyticsTable from "./ClientReportAnalyticsTable";
import JobNoteItem from "./JobNoteItem";
import JobStat from "./JobStat";

const useGetJob = (jobId: number | null) =>
  useQuery({
    queryFn: () =>
      typeof jobId !== "number"
        ? Promise.reject(new Error("invalid job id"))
        : api.company.getJobById(jobId),
    queryKey: ["JOBS", "ITEM", { jobId }],
    enabled: typeof jobId === "number",
    select: (res) => res.data,
  });

const JobDetailsDrawer = ({
  openJobDrawerId,
  setOpenJobDrawerId,
}: {
  openJobDrawerId: null | number;
  setOpenJobDrawerId: React.Dispatch<React.SetStateAction<number | null>>;
}) => {
  const router = useRouter();

  const jobId = openJobDrawerId;

  const onDrawerClose = () => {
    setOpenJobDrawerId(null);
    // setShowJobResultNavBar(true);
    router.push("/jobs");
  };

  const { data: jobDetail } = useGetJob(jobId);

  const [isCreateNote, setIsCreateNote] = useState(false);

  const [tabValue, setTabValue] = useState("Details");

  const handleTabValueChangeHandler = (value: string) => {
    setTabValue(value);
  };

  return (
    <RhDrawer
      className="flex w-[calc(100%-40px)] flex-col"
      variant="temporary"
      position="right"
      closeOnOutsideClick
      backdrop
      isOpen={openJobDrawerId !== null}
      onClose={onDrawerClose}
      size="extended"
    >
      <DrawerCloseButton
        onDrawerClose={onDrawerClose}
        openJobDrawerId={openJobDrawerId}
      />

      <Header jobDetail={jobDetail} />

      <div className="flex flex-1 overflow-auto">
        <div className="ml-tm3 mr-tm1 flex flex-1 flex-col overflow-auto">
          <RhTabs
            defaultActiveSection={tabValue}
            onTabClick={handleTabValueChangeHandler}
            className="mt-tm2 border-b-[1px] border-gray-100"
          >
            <RhTabsItem title="Details" value="Details" />
            <RhTabsItem title="Stats" value="Stats" />
            <RhTabsItem title="Report" value="Report" />
          </RhTabs>

          <div
            className={`flex-1 overflow-auto ${tabValue === "Details" ? "" : "hidden"}`}
          >
            <RhScrollbar style={{ height: "100%" }}>
              <JobDisplay jobDetail={jobDetail} />
            </RhScrollbar>
          </div>

          <div
            className={`flex-1 overflow-auto ${tabValue === "Stats" ? "" : "hidden"}`}
          >
            <RhScrollbar style={{ height: "100%" }}>
              <JobStat jobDetail={jobDetail} />
            </RhScrollbar>
          </div>

          <div
            className={`flex-1 overflow-auto ${tabValue === "Report" ? "" : "hidden"}`}
          >
            <RhScrollbar style={{ height: "100%" }}>
              <ClientReportAnalyticsTable jobId={jobDetail?.id} />
            </RhScrollbar>
          </div>
        </div>

        <div className="flex max-w-[600px] flex-1 flex-col overflow-auto">
          <div className="w-full px-tsm py-tnano">
            <h3>Notes</h3>
          </div>

          <div className="background-gray flex-1 overflow-auto px-tnano">
            <RhTimeline
              className={`relative !my-0 ml-tsm [&>.timeline-item>div]:w-full`}
              timelineCircleClass="border-none !bg-brandGreen-50 h-[28px] w-[28px] top-[-12px] left-[1px]"
              timelineLineClass="w-[1px] !bg-gray-100 top-[10px] h-[calc(100%-10px)]"
              align="right"
            >
              {isCreateNote && (
                <AddNote jobId={jobId} setIsCreateNote={setIsCreateNote} />
              )}

              {jobDetail?.notes?.map((note_item) => (
                <RhTimelineItem
                  key={note_item.id}
                  className="w-full"
                  circleComponent={
                    <RhIcon
                      icon={"tabler:notes"}
                      className="h-[16px] w-[16px] text-success-600"
                    />
                  }
                >
                  <JobNoteItem noteItem={note_item} jobId={jobId} />
                </RhTimelineItem>
              ))}
            </RhTimeline>
          </div>

          {jobDetail?.status !== "Closed" && (
            <div
              className={`flex h-[48px] justify-center gap-tm1 border border-neutral-100 px-tsm py-tpico`}
            >
              <RhTooltip title="Add Note">
                <Button
                  variant={"tertiary"}
                  onClick={() => setIsCreateNote(true)}
                >
                  <RhIcon icon="tabler:notes" />
                </Button>
              </RhTooltip>
            </div>
          )}
        </div>
      </div>
    </RhDrawer>
  );
};

export default JobDetailsDrawer;

const DrawerCloseButton = ({
  onDrawerClose,
  openJobDrawerId,
}: {
  onDrawerClose: () => void;
  openJobDrawerId: number | null;
}) => {
  return (
    <div
      onClick={onDrawerClose}
      data-testid="candidate-drawer-close"
      className={`absolute flex h-[40px] w-[40px] cursor-pointer items-center justify-center text-white ${
        openJobDrawerId !== null ? "-left-[40px]" : ""
      }`}
    >
      <RhIcon icon="tabler:x"></RhIcon>
    </div>
  );
};

const Header = ({ jobDetail }: { jobDetail: JobType | undefined }) => {
  return (
    <div className="border-b border-gray-100 px-tm3 py-tsm">
      <div className="flex items-center justify-between">
        <p className="body-small text-hint">
          {jobDetail?.title} -{" "}
          {jobDetail?.is_codemonk_handpick
            ? jobDetail?.handpick_company?.name
            : jobDetail?.company?.name}
        </p>

        <div className="flex items-center gap-tnano">
          <p className="body-small text-hint">
            Created on{" "}
            {jobDetail?.created_ts
              ? dayjs(jobDetail.created_ts).format("DD MMM YYYY")
              : "-"}
          </p>

          <div className="border-border h-[16px] border-l"></div>

          <p className="body-small text-hint">
            Last updated{" "}
            {jobDetail?.modified_ts
              ? dayjs(jobDetail.modified_ts).format("DD MMM YYYY")
              : "-"}
          </p>
        </div>
      </div>

      {(jobDetail?.status === "Active" ||
        jobDetail?.status === "Closed" ||
        jobDetail?.status === "Hold") && (
        <>
          <JobStatusProgress
            jobRequirementStatus={jobDetail?.candidate_count}
            isJobRequirementStatusLoading={false}
            className={`background-gray border-none`}
          />
          <div className="flex justify-end">
            <Button
              onClick={() =>
                window?.open(
                  `${HOST_URL}/candidates?jobId=${jobDetail?.id}`,
                  "_blank",
                )
              }
            >
              View Candidates
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

const JobDisplay = ({ jobDetail }: { jobDetail: JobType | undefined }) => {
  return (
    <div className="flex basis-[700px] flex-col overflow-auto border-r border-gray-100">
      <JobDetailsTab jobDetail={jobDetail} />
    </div>
  );
};

const JobDetailsTab = ({ jobDetail }: { jobDetail: JobType | undefined }) => {
  return (
    <div className="flex-1 space-y-tm2 overflow-auto px-tm3 pt-tm2">
      <BasicDetails jobDetail={jobDetail} />
      <RhDivider />
      <SkillsDetails jobDetail={jobDetail} />
      <RhDivider />
      <JobDescription jobDetail={jobDetail} />
      <RhDivider />
      <ScreeningQuestions jobDetail={jobDetail} />
      <RhDivider />
      <EmployerDetails jobDetail={jobDetail} />
      <RhDivider />
      <TeamDetails jobDetail={jobDetail} />
      <RhDivider />
      <WorkingHourDetails jobDetail={jobDetail} />
      <RhDivider />
      <OtherDetails jobDetail={jobDetail} />
      <RhDivider />
      <InterviewRounds jobDetail={jobDetail} />
      <RhDivider />
      <UserManagementDetails jobDetail={jobDetail} />
      <RhDivider />
      <AssociateRecruiterDetails jobDetail={jobDetail} />
      <RhDivider />
      <JobCloseStatus jobDetail={jobDetail} />
    </div>
  );
};

const BasicDetails = ({ jobDetail }: { jobDetail: JobType | undefined }) => {
  const [isEdit, setIsEdit] = useState(false);

  if (isEdit && jobDetail)
    return <BasicDetailsEdit setIsEdit={setIsEdit} jobDetail={jobDetail} />;

  return (
    <div className="space-y-tsm">
      <div className="flex items-center gap-tnano">
        <h4>Basic Details</h4>
        <RhIcon
          icon="tabler:pencil"
          onClick={() => setIsEdit(true)}
          className="cursor-pointer"
        />
      </div>

      <div className="space-y-tpico">
        <p className="text-hint">Job Title</p>
        <h5 data-testid="job-detail-title">{jobDetail?.title}</h5>
      </div>

      <div className="space-y-tpico">
        <p className="text-hint">Job Role</p>
        <h5>{jobDetail?.job_role?.name ?? "-"}</h5>
      </div>

      <div className="space-y-tpico">
        <p className="text-hint">Job Type</p>
        <h5>{jobDetail?.job_type ?? "-"}</h5>
      </div>

      <div className="space-y-tpico">
        <p className="text-hint">Experience Range</p>
        <h5>
          {formatExperienceRange.number({
            min: jobDetail?.min_exp,
            max: jobDetail?.max_exp,
          })}
        </h5>
      </div>

      {jobDetail?.is_min_exp_negotiable && (
        <div className="space-y-tpico">
          <p className="text-hint">Negotiable min experience</p>
          <h5>{jobDetail.negotiable_min_exp ?? "-"}</h5>
        </div>
      )}

      <div className="space-y-tpico">
        <p className="text-hint">
          Joining Availability<span className="body-small"> (Days)</span>
        </p>
        <h5>
          {jobDetail?.joining_availability_days
            ? jobDetail?.joining_availability_days === 999
              ? "Any"
              : jobDetail?.joining_availability_days
            : "-"}
        </h5>
      </div>

      {jobDetail?.is_joining_availability_negotiable && (
        <div className="space-y-tpico">
          <p className="text-hint">
            Negotiable Joining Availability
            <span className="body-small"> (Days)</span>
          </p>
          <h5>
            {jobDetail?.negotiable_min_joining_availability
              ? jobDetail?.negotiable_min_joining_availability === 999
                ? "Any"
                : jobDetail?.negotiable_min_joining_availability
              : "-"}
          </h5>
        </div>
      )}

      <div className="space-y-tpico">
        <p className="text-hint">Work Type</p>
        <h5>
          {jobDetail?.work_preference_new &&
          jobDetail?.work_preference_new.length !== 0
            ? `(${jobDetail?.work_preference_new?.join(", ")})`
            : "-"}{" "}
          {jobDetail?.location_new.map((city) => city.name).join(", ")}
        </h5>
      </div>

      <div className="space-y-tpico">
        <p className="text-hint">Salary Range</p>
        <h5>
          {!jobDetail?.min_salary && !jobDetail?.max_salary && "-"}

          {formatCurrencyRange({
            min: jobDetail?.min_salary,
            max: jobDetail?.max_salary,
          })}
        </h5>
      </div>

      {jobDetail?.is_max_salary_negotiate && (
        <div className="space-y-tpico">
          <p className="text-hint">{`Negotiable Max Salary (lpa)`}</p>
          <h5>{jobDetail?.negotiable_max_salary ?? "-"}</h5>
        </div>
      )}

      <div className="space-y-tpico">
        <p className="text-hint">No. of Openings</p>
        <h5>{jobDetail?.openings ?? "-"}</h5>
      </div>
    </div>
  );
};

const basicDetailValidation = Yup.object().shape({
  title: Yup.string()
    .min(3, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  job_role: Yup.object().required("Required").typeError("Required"),
  min_exp: Yup.number()
    .required("Required")
    .min(0, "Invalid")
    .typeError("Required"),
  max_exp: Yup.number().when("is_experience_range_any", {
    is: false,
    then: Yup.number().required("Required").typeError("Required"),
    otherwise: Yup.number().notRequired(),
  }),
  negotiable_min_exp: Yup.number().when("is_min_exp_negotiable", {
    is: true,
    then: Yup.number().required("Required").typeError("Required"),
  }),
  min_salary: Yup.number()
    .required("Required")
    .min(0, "Invalid")
    .lessThan(Yup.ref("max_salary"), "Min must be less than max"),
  max_salary: Yup.number().required("Required").typeError("Required"),
  negotiable_max_salary: Yup.number().when("is_max_salary_negotiate", {
    is: true,
    then: Yup.number().required("Required").typeError("Required"),
  }),
  openings: Yup.number().required("Required").typeError("Required"),
  joining_availability_days__form_value: Yup.object()
    .required("Required")
    .typeError("Required"),

  negotiable_min_joining_availability__form_value: Yup.object().when(
    "is_joining_availability_negotiable",
    {
      is: true,
      then: Yup.object().required("Required").typeError("Required"),
    },
  ),

  work_preference_new__form_value: Yup.object().test(
    (data: Record<string, boolean>) => Object.values(data).includes(true),
  ),
  location_new: Yup.array().when("work_preference_new__form_value", {
    is: (data: Record<LocationPreferenceEnum, boolean>) =>
      data &&
      (data[locationPreferenceValues.Hybrid] ||
        data[locationPreferenceValues.Onsite]),
    then: Yup.array().min(1, "Atleast one location is required"),
  }),
});

const BasicDetailsEdit = ({
  setIsEdit,
  jobDetail,
}: {
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
  jobDetail: JobType;
}) => {
  const initialFormValue: JobFormType = transformJobDataToFormData(jobDetail);

  const queryClient = useQueryClient();

  const patchJob = useMutation({
    mutationFn: async (formData: JobFormType) => {
      const payload = transformFormDataToJobData(formData, [
        "title",
        "job_role",
        "job_type",
        "min_exp",
        "max_exp",
        "negotiable_min_exp",
        "min_salary",
        "max_salary",
        "openings",
        "joining_availability_days",
        "negotiable_min_joining_availability",
        "negotiable_max_salary",
        "location_new",
        "work_preference_new",
        "is_experience_range_any",
        "is_min_exp_negotiable",
        "is_joining_availability_negotiable",
        "is_max_salary_negotiate",
      ]);
      return await api.company.patchJob(payload);
    },
    onSuccess: () => {
      setIsEdit(false);
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "ITEM", { jobId: jobDetail.id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "LIST"],
      });

      RhToast.success("Job updated successfully", {
        position: "bottom-right",
      });
    },
  });

  return (
    <Formik
      initialValues={initialFormValue}
      validationSchema={basicDetailValidation}
      onSubmit={patchJob.mutate}
    >
      {({ values, setFieldValue, errors, touched, submitCount }) => (
        <Form className="space-y-tm3">
          <h4>Basic Details</h4>
          {JSON.stringify(errors)}
          <div>
            <RhInputFormik
              block
              type="text"
              name="title"
              label="Job Title"
              className="max-w-[320px]"
            />
          </div>
          <div className="max-w-[320px]">
            <RhLabel required>Job Role</RhLabel>

            <DataSelectFormik
              valueKey={"job_role"}
              apiCall={jobRoleSearchApiCall}
              setFormikFieldValue={setFieldValue}
              values={values}
            />
          </div>
          <div>
            <RhLabel required>Job Type</RhLabel>
            <RadioInputFormik
              alignment="Horizontal"
              options={[
                { value: "Full-Time", label: "Full time" },
                { value: "Contract", label: "Contract" },
                {
                  value: "Internship",
                  label: "Internship",
                },
                {
                  value: "Freelancer",
                  label: "Freelancer",
                },
              ]}
              name={`job_type`}
            />
          </div>
          <div className="max-w-[320px]">
            <RhLabel required>Experience Range (y)</RhLabel>
            <div className="flex">
              <div className="flex-1">
                <RhLabel>Min</RhLabel>
                <RhInputFormik
                  name="min_exp"
                  className="w-full"
                  type={"number"}
                  min={0}
                  onWheel={(e: any) => e.target.blur()}
                />
              </div>
              <span className="m-tnano mt-tm3">-</span>
              <div className="flex-1">
                <RhLabel>Max</RhLabel>
                <RhInputFormik
                  name="max_exp"
                  className="w-full"
                  type={"number"}
                  onWheel={(e: any) => e.target.blur()}
                />
                <div className="flex items-center gap-tnano">
                  <RhInputFormik
                    name="is_experience_range_any"
                    type={"checkbox"}
                    onClick={() => setFieldValue("max_exp", "")}
                  />
                  <p>Any</p>
                </div>
              </div>
            </div>
            <div className="">
              <div className="flex items-center gap-tnano">
                <RhInputFormik
                  name="is_min_exp_negotiable"
                  type={"checkbox"}
                  onClick={() => setFieldValue("negotiable_min_exp", "")}
                />
                <p>Negotiable minimum experience</p>
              </div>

              {values.is_min_exp_negotiable && (
                <div className="mt-tnano">
                  <RhLabel required>Negotiable Min Experience</RhLabel>
                  <RhInputFormik
                    name="negotiable_min_exp"
                    type="number"
                    className="max-w-[120px]"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="max-w-[320px]">
            <RhLabel required>Joining Availability</RhLabel>
            <RhSelectFormik
              name="joining_availability_days__form_value"
              options={joiningAvailabilityOptions}
            />
            <div className="flex items-center gap-tnano">
              <RhInputFormik
                name="is_joining_availability_negotiable"
                type={"checkbox"}
                onClick={() =>
                  setFieldValue(
                    "negotiable_min_joining_availability__form_value",
                    undefined,
                  )
                }
              />
              <p>Negotiable minimum joining availability</p>
            </div>
            {values.is_joining_availability_negotiable && (
              <>
                <RhLabel required className="!mt-tnano">
                  Negotiable Joining Availability
                </RhLabel>
                <RhSelectFormik
                  name="negotiable_min_joining_availability__form_value"
                  options={joiningAvailabilityOptions.filter(
                    (item) => item.value !== "0",
                  )}
                  menuPortalTarget={
                    typeof window !== "undefined" &&
                    document.querySelector("#theme-provider")
                  }
                />
              </>
            )}
          </div>
          <div>
            <RhLabel required>Work Type</RhLabel>
            <RhFormGroup>
              <div className="mb-tsm flex gap-tm2">
                {values.work_preference_new__form_value &&
                  Object.keys(values.work_preference_new__form_value).map(
                    (item, idx) => (
                      <RhFormGroupItem
                        key={idx}
                        control={
                          <RhInputFormik
                            type="checkbox"
                            name={`work_preference_new__form_value[${item}]`}
                            error={
                              (touched.work_preference_new__form_value ||
                                submitCount > 1) &&
                              !!errors.work_preference_new__form_value
                            }
                          />
                        }
                        label={item}
                      />
                    ),
                  )}
                <span className="text-xs text-red-500">
                  {(touched.work_preference_new__form_value ||
                    submitCount > 1) &&
                    !!errors.work_preference_new__form_value &&
                    "Please select job location type"}
                </span>
              </div>
            </RhFormGroup>
          </div>
          {values.work_preference_new__form_value &&
            (values.work_preference_new__form_value.Hybrid ||
              values.work_preference_new__form_value.Onsite) && (
              <div className="max-w-[320px]">
                <RhLabel required>Work Location</RhLabel>
                <DataSelectFormik
                  className="z-10"
                  values={values}
                  valueKey={`location_new`}
                  setFormikFieldValue={setFieldValue}
                  apiCall={citySearchApiCall}
                  isMulti
                  placeholder="Search"
                  {...propToFormatCityLabelWithRhSelect}
                />
              </div>
            )}
          <div className="max-w-[320px]">
            <RhLabel required>Salary Range (lpa)</RhLabel>
            <div className="flex">
              <div className="flex-1">
                <RhLabel>Min</RhLabel>
                <RhInputFormik
                  name="min_salary"
                  className="w-full"
                  type={"number"}
                  onWheel={(e: any) => e.target.blur()}
                />
              </div>
              <span className="m-tnano mt-tm3">-</span>
              <div className="flex-1">
                <RhLabel>Max</RhLabel>
                <RhInputFormik
                  name="max_salary"
                  className="w-full"
                  type={"number"}
                  onWheel={(e: any) => e.target.blur()}
                />
              </div>
            </div>
            <div className="flex items-center gap-tnano">
              <RhInputFormik
                name="is_max_salary_negotiate"
                type={"checkbox"}
                onClick={() => setFieldValue("negotiable_max_salary", "")}
              />
              <p>Negotiable Max Salary</p>
            </div>
            {values.is_max_salary_negotiate && (
              <div className="mt-tnano">
                <RhLabel required>Negotiable Max Salary (lpa)</RhLabel>
                <RhInputFormik
                  name="negotiable_max_salary"
                  type="number"
                  className="max-w-[120px]"
                />
              </div>
            )}
          </div>
          <div>
            <RhLabel required>Number of Openings</RhLabel>
            <RhInputFormik name="openings" type="number" />
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant={"tertiary"}
              onClick={() => setIsEdit(false)}
            >
              Cancel
            </Button>

            <Button type="submit">Save</Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

const SkillsDetails = ({ jobDetail }: { jobDetail: JobType | undefined }) => {
  const [isEdit, setIsEdit] = useState(false);

  if (isEdit && jobDetail)
    return <SkillsDetailsEdit setIsEdit={setIsEdit} jobDetail={jobDetail} />;

  return (
    <div className="space-y-tsm">
      <div className="flex items-center gap-tnano">
        <h4>Skills</h4>
        <RhIcon
          icon="tabler:pencil"
          onClick={() => setIsEdit(true)}
          className="cursor-pointer"
        />
      </div>

      {jobDetail?.priority_skills?.skills_in_priority?.map(
        (skillGroup, idx) => (
          <div className="space-y-tpico" key={idx}>
            <p className="text-hint">{`Priority ${idx + 1}`}</p>
            <div className="flex items-center gap-tpico">
              {skillGroup.map((skillInfo) => (
                <Chip key={skillInfo.skill.id} size="sm">
                  {skillInfo.skill.name}{" "}
                  {skillInfo.min_exp ? `(${skillInfo.min_exp}y)` : ""}
                </Chip>
              ))}
            </div>
          </div>
        ),
      )}

      <div className="space-y-tpico">
        <p className="text-hint">Non Deal Breaker Skills</p>
        <div className="flex items-center gap-tpico">
          {jobDetail?.priority_skills?.non_deal_breaker_skills?.map(
            (skillInfo) => (
              <Chip key={skillInfo.skill.id} size="sm">
                {skillInfo.skill.name}{" "}
                {skillInfo.min_exp ? `(${skillInfo.min_exp}y)` : ""}
              </Chip>
            ),
          )}

          {(!jobDetail?.priority_skills.non_deal_breaker_skills ||
            jobDetail.priority_skills.non_deal_breaker_skills.length === 0) && (
            <p>-</p>
          )}
        </div>
      </div>
    </div>
  );
};

const SkillsDetailsEdit = ({
  jobDetail,
  setIsEdit,
}: {
  jobDetail: JobType;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const initialFormValue: JobFormType = transformJobDataToFormData(jobDetail);

  const queryClient = useQueryClient();

  const patchJob = useMutation({
    mutationFn: async (formData: JobFormType) => {
      const payload = transformFormDataToJobData(formData, [
        "priority_skills",
        "primary_skills",
        "other_skills",
      ]);
      return await api.company.patchJob(payload);
    },
    onSuccess: () => {
      setIsEdit(false);
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "ITEM", { jobId: jobDetail.id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "LIST"],
      });

      RhToast.success("Job updated successfully", {
        position: "bottom-right",
      });
    },
  });

  return (
    <Formik initialValues={initialFormValue} onSubmit={patchJob.mutate}>
      {({ values, setFieldValue }) => (
        <Form className="space-y-tm3">
          <h4>Skills</h4>

          <JobSkillPrioritization
            values={values}
            setFieldValue={setFieldValue}
          />

          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant={"tertiary"}
              onClick={() => setIsEdit(false)}
            >
              Cancel
            </Button>

            <Button type="submit">Save</Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

const JobDescription = ({ jobDetail }: { jobDetail: JobType | undefined }) => {
  const [isEdit, setIsEdit] = useState(false);

  if (isEdit && jobDetail)
    return <JobDescriptionEdit setIsEdit={setIsEdit} jobDetail={jobDetail} />;

  return (
    <div className="space-y-tsm">
      <div className="flex items-center gap-tnano">
        <h4>Job Description</h4>
        <RhIcon
          icon="tabler:pencil"
          onClick={() => setIsEdit(true)}
          className="cursor-pointer"
        />
      </div>

      <RichTextViewer richText={jobDetail?.job_description ?? "-"} />
    </div>
  );
};

const JobDescriptionEdit = ({
  jobDetail,
  setIsEdit,
}: {
  jobDetail: JobType;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const initialFormValue: JobFormType = transformJobDataToFormData(jobDetail);

  const queryClient = useQueryClient();

  const patchJob = useMutation({
    mutationFn: async (formData: JobFormType) => {
      const payload = transformFormDataToJobData(formData, ["job_description"]);
      return await api.company.patchJob(payload);
    },
    onSuccess: () => {
      setIsEdit(false);
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "ITEM", { jobId: jobDetail.id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "LIST"],
      });

      RhToast.success("Job updated successfully", {
        position: "bottom-right",
      });
    },
  });

  return (
    <Formik initialValues={initialFormValue} onSubmit={patchJob.mutate}>
      {({ values, setFieldValue }) => (
        <Form className="space-y-tm3">
          <h4>Job Description</h4>
          <div>
            <RichTextEditor
              value={values.job_description}
              onChange={(value) => {
                setFieldValue("job_description", value);
              }}
            />
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant={"tertiary"}
              onClick={() => setIsEdit(false)}
            >
              Cancel
            </Button>

            <Button type="submit">Save</Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

const ScreeningQuestions = ({
  jobDetail,
}: {
  jobDetail: JobType | undefined;
}) => {
  const [isEdit, setIsEdit] = useState(false);

  if (isEdit && jobDetail)
    return (
      <ScreeningQuestionsEdit setIsEdit={setIsEdit} jobDetail={jobDetail} />
    );

  return (
    <div className="space-y-tsm">
      <div className="flex items-center gap-tnano">
        <h4>Screening questions</h4>
        <RhIcon
          icon="tabler:pencil"
          onClick={() => setIsEdit(true)}
          className="cursor-pointer"
        />
        <p className="text-hint">(These questions will be shown in call log)</p>
      </div>

      <RichTextViewer richText={jobDetail?.screening_questions ?? "-"} />
    </div>
  );
};

const ScreeningQuestionsEdit = ({
  jobDetail,
  setIsEdit,
}: {
  jobDetail: JobType;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const initialFormValue: JobFormType = transformJobDataToFormData(jobDetail);

  const queryClient = useQueryClient();

  const patchJob = useMutation({
    mutationFn: async (formData: JobFormType) => {
      const payload = transformFormDataToJobData(formData, [
        "screening_questions",
      ]);
      return await api.company.patchJob(payload);
    },
    onSuccess: () => {
      setIsEdit(false);
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "ITEM", { jobId: jobDetail.id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "LIST"],
      });

      RhToast.success("Job updated successfully", {
        position: "bottom-right",
      });
    },
  });

  return (
    <Formik initialValues={initialFormValue} onSubmit={patchJob.mutate}>
      {({ values, setFieldValue }) => (
        <Form className="space-y-tm3">
          <h4>Screening questions</h4>
          <div>
            <RichTextEditor
              value={values.screening_questions}
              onChange={(value) => {
                setFieldValue("screening_questions", value);
              }}
            />
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant={"tertiary"}
              onClick={() => setIsEdit(false)}
            >
              Cancel
            </Button>

            <Button type="submit">Save</Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

const EmployerDetails = ({ jobDetail }: { jobDetail: JobType | undefined }) => {
  const [isEdit, setIsEdit] = useState(false);

  if (isEdit && jobDetail)
    return <EmployerDetailsEdit setIsEdit={setIsEdit} jobDetail={jobDetail} />;

  return (
    <div className="space-y-tsm">
      <div className="flex items-center gap-tnano">
        <h4>Employer Details</h4>
        <RhIcon
          icon="tabler:pencil"
          onClick={() => setIsEdit(true)}
          className="cursor-pointer"
        />
      </div>

      <div className="space-y-tpico">
        <p className="text-hint">Hiring for</p>
        <h5>
          {jobDetail?.is_codemonk_handpick ? "Codemonk Handpicked" : "Zelevate"}
        </h5>
      </div>

      <div className="space-y-tpico">
        <p className="text-hint">Employer Name</p>
        <h5>
          {jobDetail?.is_codemonk_handpick
            ? (jobDetail?.handpick_company?.name ?? "")
            : (jobDetail?.company?.name ?? "-")}
        </h5>
      </div>
    </div>
  );
};

const employerDetailValidationSchema = Yup.object().shape({
  company: Yup.object().required("Required"),
  handpick_company: Yup.object().when("is_codemonk_handpick", {
    is: "true",
    then: Yup.object().required("Required").typeError("Required"),
  }),
});

const EmployerDetailsEdit = ({
  jobDetail,
  setIsEdit,
}: {
  jobDetail: JobType;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const initialFormValue: JobFormType = transformJobDataToFormData(jobDetail);

  const queryClient = useQueryClient();

  const patchJob = useMutation({
    mutationFn: async (formData: JobFormType) => {
      const payload = transformFormDataToJobData(formData, [
        "is_codemonk_handpick",
        "handpick_company.id",
        "is_public",
        "sensitive_fields",
      ]);
      return await api.company.patchJob(payload);
    },
    onSuccess: () => {
      setIsEdit(false);
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "ITEM", { jobId: jobDetail.id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "LIST"],
      });

      RhToast.success("Job updated successfully", {
        position: "bottom-right",
      });
    },
  });

  return (
    <Formik
      initialValues={initialFormValue}
      validationSchema={employerDetailValidationSchema}
      onSubmit={patchJob.mutate}
    >
      {({ values }) => (
        <Form className="space-y-tm3">
          <h4>Employer Details</h4>

          <div>
            <RhLabel>Is the hiring for?</RhLabel>
            <RadioInputFormik
              name="is_codemonk_handpick"
              options={[
                {
                  label: "Zelevate",
                  value: "false",
                },
                {
                  label: "Codemonk",
                  value: "true",
                },
              ]}
              alignment="Horizontal"
            />
          </div>

          <>
            <div className="max-w-[320px]">
              <RhLabel required>Employer Name</RhLabel>
              <CompanySelectFormik
                isDisabled
                showOnlyClients
                name={"company"}
              />
              {values.is_codemonk_handpick === "true" && (
                <div className="flex items-center gap-tmini">
                  <RhIcon icon="tabler:info-circle"></RhIcon>
                  <p>
                    You have selected <strong>Codemonk Handpicked</strong>
                    {`. Please
    select 'Codemonk' in Company field above`}
                  </p>
                </div>
              )}
            </div>

            {values.is_codemonk_handpick === "true" && (
              <div>
                <RhLabel required>Codemonk Client</RhLabel>
                <CompanySelectFormik
                  showOnlyClients
                  name={"handpick_company"}
                  className="w-[320px]"
                />
              </div>
            )}
          </>

          <div>
            <RhLabel required>Should the job be?</RhLabel>
            <RadioInputFormik
              name="is_public"
              options={[
                {
                  label: "Public (visible to all)",
                  value: "true",
                },
                {
                  label: "Private (visible to recruiters only)",
                  value: "false",
                },
              ]}
              alignment="Horizontal"
            />
          </div>

          {values.is_public === "true" && (
            <div>
              <RhLabel>Sensitive Fields</RhLabel>
              <CheckBoxInputFormik
                name="sensitive_fields"
                options={[
                  {
                    label: "Min Salary",
                    value: "min_salary",
                  },
                  {
                    label: "Max Salary",
                    value: "max_salary",
                  },
                ]}
                alignment="Horizontal"
              />
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant={"tertiary"}
              onClick={() => setIsEdit(false)}
            >
              Cancel
            </Button>

            <Button type="submit">Save</Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

const TeamDetails = ({ jobDetail }: { jobDetail: JobType | undefined }) => {
  const [isEdit, setIsEdit] = useState(false);

  if (isEdit && jobDetail)
    return <TeamDetailsEdit setIsEdit={setIsEdit} jobDetail={jobDetail} />;

  return (
    <div className="space-y-tsm">
      <div className="flex items-center gap-tnano">
        <h4>Team Size</h4>
        <RhIcon
          icon="tabler:pencil"
          onClick={() => setIsEdit(true)}
          className="cursor-pointer"
        />
      </div>

      <div className="space-y-tpico">
        <p className="text-hint">Team Members</p>
        <h5>{jobDetail?.team_size ?? "-"}</h5>
      </div>
    </div>
  );
};

const TeamDetailsEdit = ({
  jobDetail,
  setIsEdit,
}: {
  jobDetail: JobType;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const initialFormValue: JobFormType = transformJobDataToFormData(jobDetail);

  const queryClient = useQueryClient();

  const patchJob = useMutation({
    mutationFn: async (formData: JobFormType) => {
      const payload = transformFormDataToJobData(formData, ["team_size"]);
      return await api.company.patchJob(payload);
    },
    onSuccess: () => {
      setIsEdit(false);
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "ITEM", { jobId: jobDetail.id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "LIST"],
      });

      RhToast.success("Job updated successfully", {
        position: "bottom-right",
      });
    },
  });

  return (
    <Formik initialValues={initialFormValue} onSubmit={patchJob.mutate}>
      <Form className="space-y-tm3">
        <h4>Job Description</h4>
        <div>
          <div>
            <RhInputFormik
              type="number"
              name="team_size"
              label="Team Members"
              isOptional
              className="max-w-[320px]"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant={"tertiary"}
            onClick={() => setIsEdit(false)}
          >
            Cancel
          </Button>

          <Button type="submit">Save</Button>
        </div>
      </Form>
    </Formik>
  );
};

const WorkingHourDetails = ({
  jobDetail,
}: {
  jobDetail: JobType | undefined;
}) => {
  const [isEdit, setIsEdit] = useState(false);

  if (isEdit && jobDetail)
    return (
      <WorkingHourDetailsEdit setIsEdit={setIsEdit} jobDetail={jobDetail} />
    );

  return (
    <div className="space-y-tsm">
      <div className="flex items-center gap-tnano">
        <h4>Working Hours & Days</h4>
        <RhIcon
          icon="tabler:pencil"
          onClick={() => setIsEdit(true)}
          className="cursor-pointer"
        />
      </div>

      <div className="space-y-tpico">
        <p className="text-hint">Working Hours</p>
        <h5>
          {jobDetail?.working_hours_start} - {jobDetail?.working_hours_end}
        </h5>
      </div>

      <div className="space-y-tpico">
        <p className="text-hint">Working days</p>
        <h5>{jobDetail?.working_days ?? "-"}</h5>
      </div>
    </div>
  );
};

const WorkingHourDetailsEdit = ({
  jobDetail,
  setIsEdit,
}: {
  jobDetail: JobType;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const initialFormValue: JobFormType = transformJobDataToFormData(jobDetail);

  const queryClient = useQueryClient();

  const patchJob = useMutation({
    mutationFn: async (formData: JobFormType) => {
      const payload = transformFormDataToJobData(formData, [
        "working_days",
        "working_hours_end",
        "working_hours_start",
      ]);
      return await api.company.patchJob(payload);
    },
    onSuccess: () => {
      setIsEdit(false);
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "ITEM", { jobId: jobDetail.id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "LIST"],
      });

      RhToast.success("Job updated successfully", {
        position: "bottom-right",
      });
    },
  });

  return (
    <Formik initialValues={initialFormValue} onSubmit={patchJob.mutate}>
      <Form className="space-y-tm3">
        <h4>Job Description</h4>

        <div>
          <RhLabel>Working Hours</RhLabel>

          <div className="flex max-w-[320px] items-center gap-tnano">
            <div className="flex-1">
              <RhSelectFormik
                name="working_hours_start__form_value"
                options={timeOptions}
                className="w-full"
                placeholder=""
              />
            </div>

            <span className="mt-[-20px]">-</span>

            <div className="flex-1">
              <RhSelectFormik
                name="working_hours_end__form_value"
                options={timeOptions}
                placeholder=""
                className="w-full"
              />
            </div>
          </div>
        </div>

        <div className="max-w-[320px]">
          <RhInputFormik
            name="working_days"
            type="text"
            label="Working Days"
            block
            isOptional
          />
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant={"tertiary"}
            onClick={() => setIsEdit(false)}
          >
            Cancel
          </Button>

          <Button type="submit">Save</Button>
        </div>
      </Form>
    </Formik>
  );
};

const OtherDetails = ({ jobDetail }: { jobDetail: JobType | undefined }) => {
  const [isEdit, setIsEdit] = useState(false);

  if (isEdit && jobDetail)
    return <OtherDetailsEdit setIsEdit={setIsEdit} jobDetail={jobDetail} />;

  return (
    <div className="space-y-tsm">
      <div className="flex items-center gap-tnano">
        <h4>Other Details</h4>
        <RhIcon
          icon="tabler:pencil"
          onClick={() => setIsEdit(true)}
          className="cursor-pointer"
        />
      </div>

      <div className="space-y-tpico">
        <p className="text-hint">Target Date</p>
        <h5>
          {jobDetail?.target_date
            ? dayjs(jobDetail?.target_date).format("DD MMM YYYY")
            : "-"}
        </h5>
      </div>

      <div className="space-y-tpico">
        <p className="text-hint">Job Address</p>
        <h5>{jobDetail?.job_address?.name ?? "-"}</h5>
      </div>
    </div>
  );
};

const OtherDetailsEdit = ({
  jobDetail,
  setIsEdit,
}: {
  jobDetail: JobType;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const initialFormValue: JobFormType = transformJobDataToFormData(jobDetail);

  const queryClient = useQueryClient();

  const patchJob = useMutation({
    mutationFn: async (formData: JobFormType) => {
      const payload = transformFormDataToJobData(formData, [
        "target_date",
        "job_address",
      ]);
      return await api.company.patchJob(payload);
    },
    onSuccess: () => {
      setIsEdit(false);
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "ITEM", { jobId: jobDetail.id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "LIST"],
      });

      RhToast.success("Job updated successfully", {
        position: "bottom-right",
      });
    },
  });

  return (
    <Formik initialValues={initialFormValue} onSubmit={patchJob.mutate}>
      {({ values, setFieldValue }) => (
        <Form className="space-y-tm3">
          <h4>Job Description</h4>
          <div>
            <RhLabel>Target Date</RhLabel>
            <div className="[&>div]:w-[320px]">
              <RhDatePickerFormik
                options={{
                  enableTime: false,
                  minDate: dayjs().format(),
                }}
                name="target_date"
              ></RhDatePickerFormik>
            </div>
          </div>

          <div>
            <RhLabel>Job Address</RhLabel>
            <div className="[&>div]:w-[320px]">
              <DataSelectFormik
                apiCall={citySearchApiCall}
                setFormikFieldValue={setFieldValue}
                values={values}
                valueKey={"job_address"}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant={"tertiary"}
              onClick={() => setIsEdit(false)}
            >
              Cancel
            </Button>

            <Button type="submit">Save</Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

const InterviewRounds = ({ jobDetail }: { jobDetail: JobType | undefined }) => {
  const [isEdit, setIsEdit] = useState(false);

  if (isEdit && jobDetail)
    return <InterviewRoundsEdit setIsEdit={setIsEdit} jobDetail={jobDetail} />;

  return (
    <div className="space-y-tsm">
      <div className="flex items-center gap-tnano">
        <h4>Interview Details</h4>
        <RhIcon
          icon="tabler:pencil"
          onClick={() => setIsEdit(true)}
          className="cursor-pointer"
        />
      </div>

      <div className="space-y-tpico">
        <p className="text-hint">Interview Rounds</p>

        <div className="flex items-center gap-tpico">
          {jobDetail?.interview_rounds?.map((item) => (
            <Chip size={"sm"} key={item.id}>
              {item.interview_round.name}
            </Chip>
          ))}

          {(!jobDetail?.interview_rounds ||
            jobDetail.interview_rounds.length === 0) && <p>-</p>}
        </div>
      </div>
    </div>
  );
};

const interviewerRoundValidationSchema = Yup.object().shape({
  interview_rounds: Yup.array()
    .min(1, "At least one interview round is required")
    .of(
      Yup.object().shape({
        id: Yup.string().required("ID is required"),
        name: Yup.string().required("Name is required"),
        category: Yup.object().required("Category is required"),
      }),
    ),
});

const InterviewRoundsEdit = ({
  setIsEdit,
  jobDetail,
}: {
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
  jobDetail: JobType;
}) => {
  const initialFormValue: JobFormType = transformJobDataToFormData(jobDetail);

  const queryClient = useQueryClient();

  const patchJob = useMutation({
    mutationFn: async (formData: JobFormType) => {
      const payload = transformFormDataToJobData(formData, [
        "interview_rounds",
      ]);
      return await api.company.patchJob(payload);
    },
    onSuccess: () => {
      setIsEdit(false);
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "ITEM", { jobId: jobDetail.id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "LIST"],
      });

      RhToast.success("Job updated successfully", {
        position: "bottom-right",
      });
    },
  });

  const { data: interviewRoundCategories } = useQuery({
    queryFn: api.company.getInterviewRoundCategories,
    queryKey: ["INTERVIEW_ROUND_CATEGORIES"],
    select: (res) =>
      res.data.map((item: { name: string; id: number }) => ({
        label: item.name,
        value: item.id.toString(),
      })),
  });

  return (
    <Formik
      initialValues={initialFormValue}
      onSubmit={patchJob.mutate}
      validationSchema={interviewerRoundValidationSchema}
    >
      {({ values, setFieldValue, errors }) => (
        <Form className="space-y-tm3">
          <h4>Interview Details</h4>

          <div className="my-tsm">
            <DragDropContext
              onDragEnd={(result) => {
                if (!result.destination) return;

                const items = [...(values.interview_rounds ?? [])];
                const [reorderedItem] = items.splice(result.source.index, 1);
                items.splice(result.destination.index, 0, reorderedItem);

                setFieldValue("interview_rounds", items);
              }}
            >
              <Droppable droppableId="job-interview-round">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {values.interview_rounds?.map((interview_round, index) => (
                      <Draggable
                        key={interview_round.id}
                        draggableId={interview_round.id?.toString() ?? ""}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={`flex !cursor-pointer items-center gap-tm1 px-tnano`}
                          >
                            <img
                              className="cursor-grab"
                              src="/icons/gripVertical.svg"
                            />

                            <div className="w-[200px]">
                              <RhLabel required>Interview Category</RhLabel>
                              <RhSelectFormik
                                name={`interview_rounds[${index}].category`}
                                options={interviewRoundCategories}
                              />
                            </div>

                            <div className="flex-1">
                              <RhLabel required>Interview Name</RhLabel>
                              <RhInputFormik
                                name={`interview_rounds[${index}].name`}
                                block
                              />
                            </div>

                            <Button
                              variant="tertiaryDanger"
                              type="button"
                              onClick={() => {
                                const newData = [...values.interview_rounds];
                                newData.splice(index, 1);
                                setFieldValue("interview_rounds", newData);
                              }}
                            >
                              <RhIcon icon="tabler:trash" />
                            </Button>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    <>{provided.placeholder}</>
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            <div className="mt-tnano"></div>
            <Button
              variant={"tertiary"}
              onClick={() => {
                setFieldValue("interview_rounds", [
                  ...(values.interview_rounds ?? []),
                  {
                    id: Math.random(),
                    name: "",
                    category: undefined,
                  },
                ]);
              }}
              type="button"
            >
              <RhIcon icon="tabler:plus" />
              Add
            </Button>

            <p className="body-small text-red-500">
              {typeof errors.interview_rounds === "string"
                ? errors.interview_rounds
                : ""}
            </p>
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant={"tertiary"}
              onClick={() => setIsEdit(false)}
            >
              Cancel
            </Button>

            <Button type="submit">Save</Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

const UserManagementDetails = ({
  jobDetail,
}: {
  jobDetail: JobType | undefined;
}) => {
  const [isEdit, setIsEdit] = useState(false);

  if (isEdit && jobDetail)
    return (
      <UserManagementDetailsEdit setIsEdit={setIsEdit} jobDetail={jobDetail} />
    );

  return (
    <div className="space-y-tsm">
      <div className="flex items-center gap-tnano">
        <h4>Employer Hiring Contacts</h4>
        <RhIcon
          icon="tabler:pencil"
          onClick={() => setIsEdit(true)}
          className="cursor-pointer"
        />
      </div>

      {(!jobDetail?.contacts || jobDetail.contacts.length === 0) && <p>-</p>}

      <div className="space-y-tm2">
        {jobDetail?.contacts?.map((contact) => (
          <div
            key={contact.id}
            className="rounded-md border border-gray-100 px-tsm py-tmini"
          >
            <div className="space-y-tnano">
              <div className="flex items-center justify-between">
                <h4>{contact.name}</h4>
              </div>

              <h5>{contact?.role?.name}</h5>

              <div className="flex gap-tnano">
                {contact?.user_details?.email && (
                  <>
                    <p className="text-hint">{contact?.user_details?.email}</p>
                    <div className="h-[20px] border-l border-gray-100" />
                  </>
                )}

                {contact?.user_details?.mobile && (
                  <p className="text-hint">
                    {contact?.user_details?.mobile_code ?? ""}{" "}
                    {contact?.user_details?.mobile}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const UserManagementDetailsEdit = ({
  jobDetail,
  setIsEdit,
}: {
  jobDetail: JobType;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const initialFormValue: JobFormType = transformJobDataToFormData(jobDetail);

  const queryClient = useQueryClient();

  const patchJob = useMutation({
    mutationFn: async (formData: JobFormType) => {
      const payload = transformFormDataToJobData(formData, ["contacts"]);
      return await api.company.patchJob(payload);
    },
    onSuccess: () => {
      setIsEdit(false);
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "ITEM", { jobId: jobDetail.id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "LIST"],
      });

      RhToast.success("Job updated successfully", {
        position: "bottom-right",
      });
    },
  });
  const [isAddingNewManager, setIsAddingNewManager] = useState(false);

  return (
    <>
      <Formik initialValues={initialFormValue} onSubmit={patchJob.mutate}>
        {({ values, setFieldValue }) => (
          <Form className="space-y-tm3">
            <h4>Employer Hiring Contacts</h4>

            <div>
              <DataSelectFormik
                key={JSON.stringify({
                  id: jobDetail?.id,
                  isAddingNewManager,
                })}
                valueKey={`contacts`}
                className="w-[320px]"
                setFormikFieldValue={setFieldValue}
                values={values}
                isMulti
                apiCall={(input: string, callback) => {
                  return companyContactSearchApiCall(
                    {
                      search: input,
                      company:
                        jobDetail?.is_codemonk_handpick === false
                          ? jobDetail?.company?.id
                          : jobDetail?.handpick_company?.id,
                    },
                    callback,
                  );
                }}
                hideSelectedOptionsChips
              />
            </div>

            <div className="space-y-tpico">
              <p className="text-hint">
                Cannot find the hiring contact you are looking for?{" "}
              </p>
              <Button
                onClick={() => setIsAddingNewManager(true)}
                variant={"tertiary"}
                type="button"
              >
                <RhIcon icon="tabler:plus" />
                Add
              </Button>
            </div>

            <div className="space-y-tm2">
              {values.contacts?.map((contact) => (
                <div
                  key={contact.id}
                  className="rounded-md border border-gray-300 px-tsm py-tmini"
                >
                  <div className="space-y-tnano">
                    <div className="flex items-center justify-between">
                      <h4>{contact.name}</h4>
                      <RhIcon
                        icon="tabler:trash"
                        className="cursor-pointer text-red-600"
                        onClick={() => {
                          const newContact = values.contacts?.filter(
                            (item) => item.id !== contact.id,
                          );
                          setFieldValue("contacts", newContact);
                        }}
                      />
                    </div>

                    <h5>{contact?.role?.name}</h5>

                    <div className="flex gap-tnano">
                      {contact?.user_details?.email && (
                        <>
                          <p>{contact?.user_details?.email}</p>
                          <div className="h-[20px] border-l border-gray-600" />
                        </>
                      )}

                      {contact?.user_details?.mobile && (
                        <p>
                          {contact?.user_details?.mobile_code ?? ""}{" "}
                          {contact?.user_details?.mobile}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                variant={"tertiary"}
                onClick={() => setIsEdit(false)}
              >
                Cancel
              </Button>

              <Button type="submit">Save</Button>
            </div>
          </Form>
        )}
      </Formik>
      <CreateHiringManager
        isAddingNewManager={isAddingNewManager}
        setIsAddingNewManager={setIsAddingNewManager}
        createdJobId={jobDetail.id}
      />
    </>
  );
};

const AssociateRecruiterDetails = ({
  jobDetail,
}: {
  jobDetail: JobType | undefined;
}) => {
  const [isEdit, setIsEdit] = useState(false);

  if (isEdit && jobDetail)
    return (
      <AssociateRecruiterDetailsEdit
        setIsEdit={setIsEdit}
        jobDetail={jobDetail}
      />
    );

  return (
    <div className="space-y-tsm">
      <div className="flex items-center gap-tnano">
        <h4>Associate Recruiters</h4>
        <RhIcon
          icon="tabler:pencil"
          onClick={() => setIsEdit(true)}
          className="cursor-pointer"
        />
      </div>

      <div className="flex items-center gap-tpico">
        {jobDetail?.owner?.map((item) => (
          <Chip key={item.id} size="sm">
            {item.name}
          </Chip>
        ))}
      </div>

      {(!jobDetail?.owner || jobDetail.owner.length === 0) && <p>-</p>}
    </div>
  );
};

const AssociateRecruiterDetailsEdit = ({
  jobDetail,
  setIsEdit,
}: {
  jobDetail: JobType;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const initialFormValue: JobFormType = transformJobDataToFormData(jobDetail);

  const queryClient = useQueryClient();

  const patchJob = useMutation({
    mutationFn: async (formData: JobFormType) => {
      const payload = transformFormDataToJobData(formData, ["owner"]);
      return await api.company.patchJob(payload);
    },
    onSuccess: () => {
      setIsEdit(false);
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "ITEM", { jobId: jobDetail.id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "LIST"],
      });

      RhToast.success("Job updated successfully", {
        position: "bottom-right",
      });
    },
  });

  return (
    <>
      <Formik initialValues={initialFormValue} onSubmit={patchJob.mutate}>
        {({ values, setFieldValue }) => (
          <Form className="space-y-tm3">
            <h4>Associate Recruiters</h4>

            <div className="">
              <RecruiterSelectFormik
                setFormikFieldValue={setFieldValue}
                values={values}
                valueKey="owner"
                className="w-[320px]"
              />
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                variant={"tertiary"}
                onClick={() => setIsEdit(false)}
              >
                Cancel
              </Button>

              <Button type="submit">Save</Button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

const JobCloseStatus = ({ jobDetail }: { jobDetail: JobType | undefined }) => {
  const queryClient = useQueryClient();

  const patchJob = useMutation({
    mutationFn: async (newStatus: JobStatusEnum) =>
      typeof jobDetail === "undefined"
        ? Promise.reject(new Error("Invalid id"))
        : api.company.patchJob({ id: jobDetail.id, status: newStatus }), // Use dynamic status
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "ITEM", { jobId: jobDetail?.id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "LIST"],
      });

      RhToast.success("Job status updated", {
        position: "bottom-right",
      });
    },
  });

  if (jobDetail?.status === "Closed") {
    return (
      <div className="pb-tsm">
        <p>This job is closed</p>
      </div>
    );
  }

  return (
    <div className="space-y-tsm pb-tsm">
      <p>Set the job status</p>

      {/* Close Job */}
      <Button
        variant={"tertiaryDanger"}
        onClick={() => patchJob.mutate("Closed")}
      >
        Close Job
      </Button>

      {/* Put On-Hold or Activate Job based on status */}
      {jobDetail?.status === "Active" ? (
        <Button
          variant={"tertiaryYellow"}
          onClick={() => patchJob.mutate("Hold")}
        >
          Put On-Hold
        </Button>
      ) : (
        <Button
          variant={"tertiaryBlue"}
          onClick={() => patchJob.mutate("Active")}
        >
          Activate Job
        </Button>
      )}
    </div>
  );
};
